import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";

const ARABIC_DIGITS = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

const POPOVER_WIDTH = 200;
const POPOVER_MAX_HEIGHT = 264;

const toArabicNumber = (value: number): string =>
  String(value).replace(/\d/g, (digit) => ARABIC_DIGITS[Number(digit)]);

interface RelatedOrdersPopoverProps {
  orders: string[];
}

export default function RelatedOrdersPopover({
  orders,
}: RelatedOrdersPopoverProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < POPOVER_MAX_HEIGHT + 16;
      const left = Math.min(
        Math.max(rect.right - POPOVER_WIDTH, 8),
        window.innerWidth - POPOVER_WIDTH - 8,
      );
      setPosition({
        position: "fixed",
        left: `${left}px`,
        width: `${POPOVER_WIDTH}px`,
        ...(openUpward
          ? { bottom: `${window.innerHeight - rect.top + 6}px` }
          : { top: `${rect.bottom + 6}px` }),
      });
    };

    updatePosition();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  if (orders.length === 0) return null;

  return (
    <div ref={containerRef} className="inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50 px-2.5 py-1 text-xs font-semibold text-accent-800 transition-colors duration-200 hover:bg-accent-100 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-file-list-2-line"></i>
        {toArabicNumber(orders.length)} طلبات
        <i
          className={`ri-arrow-down-s-line transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      {open && (
        <div
          style={position}
          className="z-50 rounded-lg border border-background-200 bg-white p-1.5 shadow-pop"
        >
          <p className="px-2 py-1.5 text-[11px] font-medium text-foreground-500 border-b border-background-100 mb-1">
            الطلبات المرتبطة
          </p>
          <ul className="max-h-52 overflow-y-auto">
            {orders.map((orderId) => (
              <li key={orderId}>
                <Link
                  to={`/orders/${orderId}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between rounded-md px-2 py-1.5 text-xs font-medium text-foreground-700 transition-colors duration-150 hover:bg-primary-50 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                >
                  <span>طلب #{orderId}</span>
                  <i className="ri-arrow-left-s-line text-foreground-400"></i>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}